import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Drawer, message, Button, Skeleton, Input, Tag, Popover } from 'antd/lib';
import { useDispatch, useSelector } from 'react-redux';
import Icon, { CopyFilled, SendOutlined, PlusCircleOutlined, CheckOutlined } from '@ant-design/icons';
import classNames from 'classnames';
// eslint-disable-next-line camelcase
import { js_beautify } from 'js-beautify';
import { useDebouncedCallback } from 'use-debounce';
import styles from './ScriptCodingAssistant.module.css';
import { ReactComponent as JsonFileIcon } from './json-file.svg';
import { ReactComponent as TextFileIcon } from './text-fle.svg';
import { ReactComponent as SelectionFileIcon } from './selection-file.svg';
import { selectSignedUserProfile } from '@src/app/reducers/user/userSlice';
import platformApi, { FileContext, SelectedCode } from '@src/api/platform/platformApi';
import MarkdownViewer from '@components/MarkdownViewer/MarkdownViewer.tsx';
import { copyPlainText } from '@src/features/LogicMapper/reducers/clipboardUtils';
import useTranslation from '@hooks/useTranslation';
import { safeJsonParse } from '@common/utils';
import HoverPreviewTag from '@components/common/HoverPreviewTag/HoverPreviewTag.tsx';
import { decodeServerErrorInfo } from '@src/app/reducers/widget/utils';
import { showPremiumUpgradeModal } from '@src/features/interface/interfaceSlice';
import { SUBSCRIPTION_LIMIT_REACHED } from '@common/constants';

type ClipboardPreview = {
	type: 'text' | 'json';
	content: string;
}

type ChatMessage = {
	id: string;
	role: 'user' | 'assistant';
	content?: string;
	timestamp: number;
	// indicates the message was aborted by the user
	aborted?: boolean;
	// contains en error message if the message was aborted or there was a network error
	error?: string;
}

type ChatHistory = {
	sessionId?: string;
	// if present it indicates the user is waiting for a response
	lastPrompt?: {
		sentAt: number;
		content: string;
	}
	messages: ChatMessage[];
}


type Props = {
  defaultAttachments?: FileContext[];
  selectedCode?: SelectedCode;
  assistantDrawerVisible: boolean;
  setAssistantDrawerVisible: (visible: boolean) => void;
  getCurrentCode: () => string;
  onUpdateCode: (code: string) => void;
  onHighlightSelection?: (selectionContent: string) => void;
}

const ScriptCodingAssistant = (props: Props) => {
  const { assistantDrawerVisible, selectedCode, defaultAttachments, setAssistantDrawerVisible, getCurrentCode, onUpdateCode, onHighlightSelection } = props;
  const [chatHistory, setChatHistory] = useState<ChatHistory>({ messages: [] });
	const [currentPrompt, setCurrentPrompt] = useState('');
  const currentUser = useSelector(selectSignedUserProfile);
  const dispatch = useDispatch();
  const t = useTranslation('LogicMapper.Gates.Script.Settings.Assistant');
  const [files, setFiles] = useState<FileContext[]>(defaultAttachments || []);
  const [clipboardPreview, setClipboardPreview] = useState<ClipboardPreview | null>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);
  const lastUserScrollTimeRef = useRef<number>(0);
  const handleRemoveFile = useCallback((removedFile: FileContext) => {
    setFiles(prevFiles => prevFiles.filter(file => file.name !== removedFile.name));
  }, []);

  const scrollToBottom = useDebouncedCallback(() => {
    if ( chatPanelRef.current) {
       chatPanelRef.current.scrollTop =  chatPanelRef.current.scrollHeight;
    }
  }, 100, { maxWait: 1000 });


  const handleAddSelectionAsFile = useCallback(() => {
    if (selectedCode) {
      const { startLine, startColumn, endLine, endColumn } = selectedCode;
      const fileName = `Selection L${startLine}-L${endLine}`;
      const content = `L${startLine}:C${startColumn}-L${endLine}:C${endColumn}`;
      const newFile: FileContext = {
        name: fileName,
        content,
        type: 'selection',
      };
      setFiles(prevFiles => {
        // Avoid adding duplicate selection files if one with the exact name already exists
        if (prevFiles.find(f => f.name === fileName)) {
          return prevFiles;
        }
        return [...prevFiles, newFile];
      });
    }
  }, [selectedCode]);

  useEffect(() => {
    const checkClipboard = async () => {
      if (assistantDrawerVisible) {
        try {
          if (!document.hasFocus()) {
            // Don't clear preview if focus is temporarily lost, wait for focus event
            return;
          }

          const text = await navigator.clipboard.readText();
          if (text && text.trim() !== '') {
            const isJson = !!safeJsonParse(text);
            const currentClipboardFileType = isJson ? 'json' : 'text';
            const currentClipboardFileName = `Clipboard ${currentClipboardFileType}`;

            if (files.find(f => f.name === currentClipboardFileName && f.content === text)) {
              setClipboardPreview(null); // Already added
            } else {
              setClipboardPreview({ type: currentClipboardFileType, content: text });
            }
          } else {
            setClipboardPreview(null);
          }
        } catch (err) {
          // console.warn('Failed to read clipboard:', err);
          setClipboardPreview(null);
        }
      } else {
        setClipboardPreview(null);
      }
    };

    checkClipboard(); // Initial check

    const handleFocus = () => {
      if (assistantDrawerVisible) {
        checkClipboard();
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [assistantDrawerVisible, files]);

  const handleAssistantPromptSubmit = useCallback(async (config?: { customPrompt?: string, customHistory?: ChatHistory }) => {
    const { customPrompt, customHistory } = config || {};
    const promptToSend = customPrompt || currentPrompt;
		if (!promptToSend.trim()) { return; }
		if (!currentUser.profile?.id) { return; }

		const userMessage: ChatMessage = {
			id: Date.now().toString(),
			role: 'user',
			content: promptToSend,
			timestamp: Date.now(),
		};

    const currentHistory = customHistory || chatHistory;

		const prevSessions = [...currentHistory.messages, userMessage];
		setChatHistory(prev => ({
			...prev,
			lastPrompt: {
				sentAt: Date.now(),
				content: promptToSend,
			},
			messages: prevSessions,
		}));

		setCurrentPrompt('');

		const assistantResponse: ChatMessage = {
			id: Date.now().toString() + '-assistant',
			role: 'assistant',
			content: '',
			timestamp: Date.now(),
		};

    const code = getCurrentCode();

		const response = await platformApi.scriptCodeGen({
			currentCode: code,
			prompt: promptToSend,
			userId: currentUser.profile.id,
			sessionId: currentHistory.sessionId,
			filesContext: files,
			onSession: (session) => {
				setChatHistory(prev => ({
					...prev,
					sessionId: session,
				}));
			},
			onChunk: (chunk) => {
				assistantResponse.content += chunk;
				setChatHistory(prev => ({
					...prev,
					lastPrompt: undefined,
					messages: [...prevSessions, assistantResponse],
				}));

        const elapsedScrollTime = Date.now() - lastUserScrollTimeRef.current;
        if (elapsedScrollTime > 1000) {
          scrollToBottom();
        }
			},
		}).catch((error) => {
      const { errorCode, message } = decodeServerErrorInfo(error);

      if(errorCode === SUBSCRIPTION_LIMIT_REACHED){
        dispatch(showPremiumUpgradeModal(true));
      }

			const now = Date.now();
			setChatHistory(prev => ({
				...prev,
				lastPrompt: undefined,
				messages: [...prevSessions, { role: 'assistant', error: message || t('ErrorMessage'), timestamp: now, id: now.toString() }],
			}));

			return 'ERROR';
		});

		if (response === 'ERROR') { return; }
		// console.log('Response: ', response);

		setChatHistory(prev => {
			return {
				...prev,
				lastPrompt: undefined,
			};
		});

	}, [currentPrompt, files, chatHistory, getCurrentCode, t, scrollToBottom]);

	const handleCopyCodeToClipboard = useCallback((codeToCopy: string) => {
		copyPlainText(codeToCopy);
		message.success(t('CodeCopiedToClipboard'));
	}, [t]);

  const handleApplyCode = useCallback((codeToApply: string) => {
    const beautifiedCode = js_beautify(codeToApply, { indent_size: 2 });
		onUpdateCode(beautifiedCode);
    // Clear selections as they may not be valid anymore
    setFiles((files) => files.filter(file => file.type !== 'selection'));
	}, [onUpdateCode]);

  const handleRetry = useCallback((userPromptMessage: ChatMessage) => {
    if (!userPromptMessage || !userPromptMessage.content) {
      console.error('Retry attempt with invalid user prompt message.');
      return;
    }

    const nextHistory: ChatHistory = {
      ...chatHistory,
      messages: chatHistory.messages.slice(0, -2),
      lastPrompt: undefined,
    };

    // Remove the last two messages (user's prompt and assistant's error response)
    setChatHistory(nextHistory);

    // Resubmit the user's original prompt
    // Ensure handleAssistantPromptSubmit is called with the string content
    handleAssistantPromptSubmit({ customPrompt: userPromptMessage.content, customHistory: nextHistory });
  }, [handleAssistantPromptSubmit, chatHistory]);

  const handleNewChat = useCallback(() => {
    setChatHistory({ messages: [] });
    // Optionally, you might want to reset files as well, depending on desired behavior
    setFiles(defaultAttachments || []);
  }, [defaultAttachments]);

  const handleAddClipboardAsFile = useCallback(() => {
    if (clipboardPreview) {
      const newFile: FileContext = {
        name: `Clipboard ${clipboardPreview.type}`,
        content: clipboardPreview.content,
        type: clipboardPreview.type,
      };
      setFiles(prevFiles => {
        if (prevFiles.find(f => f.content === newFile.content)) {
          message.info(t('ClipboardItemExists', { fileName: newFile.name }));
          return prevFiles;
        }
        return [...prevFiles, newFile];
      });
      setClipboardPreview(null); // Hide virtual tag after attempting to add
    }
  }, [clipboardPreview, setFiles, t]);

  const getMarkdownFooter = useCallback((codeForFooter: string) => {
    return (
      <div className={styles.CodeFooter}>
        <Button type="text" title={t('CopyCodeToClipboard')} icon={<CopyFilled />} onClick={() => handleCopyCodeToClipboard(codeForFooter)}/>
        <Button type="text" title={t('ApplyCode')} icon={<CheckOutlined />} onClick={() => handleApplyCode(codeForFooter)}/>
      </div>
    );
  }, [t, handleCopyCodeToClipboard, handleApplyCode]);


  // Keep track of the last time the user scrolled, this in order to prevent auto-scrolling to the bottom
  // when chunks arrive.
  const handleScrollWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    lastUserScrollTimeRef.current = Date.now();
  }, []);

  return (
    <Drawer
      title={<div className={styles.TitleContainer}>
        {t('Title')}
        {chatHistory.messages.length > 1 && (
          <Button
            icon={<PlusCircleOutlined />}
            onClick={handleNewChat}
            type="text"
            title={t('NewChatBtn')}
            className={styles.NewChatButton}
          />
        )}
      </div>}
      placement="right"
      onClose={() => setAssistantDrawerVisible(false)}
      open={assistantDrawerVisible}
      getContainer={false} // Attach to the modal, or specify a different container
      style={{ position: 'absolute' }} // Required when getContainer is false
      mask={false} // Optional: if you don't want a mask
      width={500} // Adjust width as needed
      className={styles.AssistantDrawer}
      destroyOnClose // To reset state if needed, though we manage state separately
    >
      <div className={styles.ChatContainer}>
        {(chatHistory.messages.length > 0 || chatHistory.lastPrompt) && (
          <div className={styles.ChatMessages} ref={chatPanelRef} onWheel={handleScrollWheel}>
            {chatHistory.messages.map((msg, index) => (
              <div key={msg.id} className={classNames(styles.ChatMessage, styles[msg.role])}>
                <div className={classNames(styles.ChatMessageContainer, styles.MessageContainer)}>
                  {msg.role === 'user' ? (
                    <div className={styles.UserMessage}>
                      {msg.content}
                    </div>
                  ) : (
                    <>
                      {msg.content ? (
                        <MarkdownViewer
                          className={styles.MarkdownViewer}
                          markdown={msg.content}
                          docsUrl={''}
                          codeBlockFooter={getMarkdownFooter}
                        />
                      ) : (
                        <div className={styles.Error}>
                          {msg.error}
                          {index === chatHistory.messages.length - 1 && chatHistory.messages[index -1] && (
                            <Button
                              type="link"
                              onClick={() => handleRetry(chatHistory.messages[index - 1])}
                              className={styles.RetryLink}
                            >
                              {t('RetryRequestBtn')}
                            </Button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
            {chatHistory.lastPrompt && (
              <>
                <div className={classNames(styles.ChatMessage, styles.assistant)}>
                  <div className={classNames(styles.ChatMessageContainer, styles.SkeletonLoader)}>
                    <Skeleton active paragraph={{ rows: 2 }} title={false} />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {!chatHistory.messages.length && !chatHistory.lastPrompt && (
          <div className={styles.Intro}>
            {t('Description')}
          </div>
        )}

        <div className={classNames(styles.ChatInputArea, {
          [styles.ChatInputAreaBottom]: !!chatHistory.lastPrompt
          })}
        >
          <div className={styles.Attachments}>
            {files.map((file, index) => {
              const isClipboardFile = file.name.startsWith('Clipboard');
              const id = `${file.name}-${index}-${file.content.substring(0, 10)}`;
              const commonTagProps = {
                closable: true,
                onClose: () => handleRemoveFile(file),
                className: classNames(styles.FileTag, { [styles.ClickableFileTag]: file.type === 'selection' && onHighlightSelection }),
                icon: <Icon component={file.type === 'json' ? JsonFileIcon : file.type === 'selection' ? SelectionFileIcon : TextFileIcon} />,
                onClick: () => {
                  if (file.type === 'selection' && onHighlightSelection && file.content) {
                    onHighlightSelection(file.content);
                  }
                },
              };

              if (isClipboardFile && file.content) {
                return (
                  <HoverPreviewTag
                    key={id}
                    tagProps={commonTagProps}
                    previewContent={file.content}
                    previewTitle={t('ContentPreviewTitle')}
                    showAddToContextMessage={false}
                  >
                    {file.name}
                  </HoverPreviewTag>
                );
              }
              return <Tag key={file.name} {...commonTagProps}>{file.name}</Tag>;
            })}
            {clipboardPreview && (
              <HoverPreviewTag
                key="clipboard-virtual-tag"
                tagProps={{
                  onClick: handleAddClipboardAsFile,
                  className: classNames(styles.FileTag, styles.VirtualTag, styles.ClickableFileTag),
                  icon: <Icon component={clipboardPreview.type === 'json' ? JsonFileIcon : TextFileIcon} />,
                }}
                previewContent={clipboardPreview.content}
                previewTitle={t('ClipboardPreviewTitle')}
                showAddToContextMessage
              >
                {`Clipboard ${clipboardPreview.type}`}
              </HoverPreviewTag>
            )}
          </div>
          <div className={styles.TextAreaWrapper}>
            <Input.TextArea
              rows={3}
              value={currentPrompt}
              onChange={(e) => setCurrentPrompt(e.target.value)}
              placeholder={t('PromptPlaceholder')}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleAssistantPromptSubmit();
                }
              }}
              disabled={!!chatHistory.lastPrompt}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => handleAssistantPromptSubmit()}
              title={t('SendBtn')}
              loading={!!chatHistory.lastPrompt}
              className={styles.SendButton}
              disabled={!currentPrompt.trim() || !!chatHistory.lastPrompt}
            />
          </div>
          <div className={styles.SelectionButtonPlaceholder}>
            {selectedCode && (
              <Button
                type="link"
                size='small'
                onClick={handleAddSelectionAsFile}
                className={styles.AddSelectionButton}
              >
                {t('AddSelectionAsFileBtn')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default ScriptCodingAssistant;
